import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { notificationsAPI } from '../lib/api';
import { Button } from './ui/button';
import { Bell, CheckCircle2, Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const NotificationsBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchNotifs = async () => {
    try {
      const r = await notificationsAPI.list();
      setNotifications(r.data.notifications || []);
      setUnread(r.data.unread || 0);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 60000);  // poll every 60s
    return () => clearInterval(interval);
  }, []);

  const handleClickNotif = async (n) => {
    if (!n.read) {
      try { await notificationsAPI.markRead(n.id); } catch (e) { /* silent */ }
    }
    setOpen(false);
    fetchNotifs();
  };

  const markAllRead = async () => {
    await notificationsAPI.markAllRead();
    fetchNotifs();
  };

  const fmtTime = (iso) => {
    const d = new Date(iso);
    const now = Date.now();
    const diff = (now - d.getTime()) / 60000;
    if (diff < 1) return 'à l\'instant';
    if (diff < 60) return `il y a ${Math.floor(diff)} min`;
    if (diff < 1440) return `il y a ${Math.floor(diff / 60)} h`;
    return d.toLocaleDateString('fr-FR');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative p-2" data-testid="notifications-bell">
          <Bell className="w-5 h-5 text-slate-600" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1" data-testid="unread-badge">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Notifications</h3>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-xs text-orange-600 hover:text-orange-700" data-testid="mark-all-read-btn">
              <Check className="w-3 h-3 inline mr-1" /> Tout marquer lu
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-400">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              Aucune notification
            </div>
          ) : (
            notifications.map((n) => (
              <Link
                key={n.id}
                to={n.link || '#'}
                onClick={() => handleClickNotif(n)}
                className={`block px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors ${!n.read ? 'bg-orange-50/40' : ''}`}
                data-testid={`notification-${n.id}`}
              >
                <div className="flex items-start gap-3">
                  {!n.read && <span className="w-2 h-2 rounded-full bg-orange-500 mt-2 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!n.read ? 'font-semibold text-slate-900' : 'text-slate-700'} truncate`}>{n.title}</p>
                    {n.body && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.body}</p>}
                    <p className="text-[10px] text-slate-400 mt-1">{fmtTime(n.created_at)}</p>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationsBell;
